import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Leaf,
  Menu,
  X,
  ShoppingCart,
  Search,
  ChevronDown,
  MessageCircle,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  User,
} from "lucide-react";

import toast from "../utils/toast";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { loginUser, registerUser, forgetPassword } from "../api/auth";
import { getApiErrorMessage } from "../api/error";

export default function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [userMenu, setUserMenu] = useState(false);

  // api
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryOpen, setCategoryOpen] = useState(false);

  const dropdownRef = useRef(null);
  const categoryRef = useRef(null);
  const { totalItems } = useCart();
  const { isAuthenticated, login, logout } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const categoryLinks = ["Face Care", "Hair Care", "Body Care", "Combo Kits"];
  const offerItems = [
    "Free shipping on orders above ₹499",
    "Flat 10% OFF on first order",
    "100% Herbal and cruelty-free care",
    "Use code ARIGLO5 for extra savings",
  ];

  /* AUTO OPEN LOGIN (FROM PROTECTED ROUTE) */
  useEffect(() => {
    if (location.state?.openLogin) {
      setShowLogin(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  /* SCROLL EFFECT */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* CLOSE USER DROPDOWN */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserMenu(false);
      }
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* LOGIN */
  const handleLogin = async () => {
    try {
      if (!loginForm.email || !loginForm.password) {
        // return alert("All fields are required");
        return toast.error("All fields are required");
      }

      const { data } = await loginUser(loginForm);

      login(data.user || null, data.token);
      setShowLogin(false);
      setLoginForm({ email: "", password: "" });

      toast.success("Login successful 🎉");

      navigate("/dashboard", { replace: true });

    } catch (error) {
      toast.error(getApiErrorMessage(error, "Login failed"));
    }
  };

  const handleRegister = async () => {
    try {
      if (!registerForm.name || !registerForm.email || !registerForm.password) {
        return toast.error("All fields are required");
      }

      if (registerForm.password.length < 6) {
        return toast.error("Password must be at least 6 characters");
      }

      const { data } = await registerUser(registerForm);

      login(data.user || null, data.token);
      setShowRegister(false);
      setRegisterForm({ name: "", email: "", password: "" });

      toast.success("Registration successful 🎉");

      navigate("/dashboard", { replace: true });

    } catch (error) {
      toast.error(getApiErrorMessage(error, "Registration failed"));
    }
  };

  const handleForgotPassword = async () => {
    try {
      if (!forgotEmail) {
        return toast.error("Email is required");
      }

      setForgotLoading(true); // 🔥 disable button

      await forgetPassword({ email: forgotEmail });

      toast.success("Password reset link sent to your email");

      setShowForgot(false);
      setForgotEmail("");

    } catch (error) {
      toast.error(getApiErrorMessage(error, "Something went wrong"));
    } finally {
      setForgotLoading(false); // 🔥 enable again
    }
  };






  /* LOGOUT */
  const handleLogout = () => {
    logout();
    setUserMenu(false);
    navigate("/");
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    navigate(`/products?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
    setMenuOpen(false);
  };

  const handleCategoryClick = (category) => {
    navigate(`/products?category=${encodeURIComponent(category)}`);
    setCategoryOpen(false);
    setMenuOpen(false);
  };

  return (
    <>
      {/* TOP BAR */}
      <div className="topbar">
        <div className="container topbar-inner">
          <div className="top-left">
            <span><Phone size={12} /> +91 9753346858</span>
            <span><Mail size={12} /> support@ariglo.com</span>
          </div>

          <div className="top-center font-sans" aria-label="Current offers">
            <div className="offer-marquee">
              <div className="offer-track">
                {[...offerItems, ...offerItems].map((offer, index) => (
                  <span key={`${offer}-${index}`} className="offer-item">
                    {offer}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="top-right">
            <a
              href="https://www.instagram.com/arigloharbal"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
            >
              <Instagram size={14} />
            </a>

            <a
              href="https://www.facebook.com/profile.php?id=61586956569400"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
            >
              <Facebook size={14} />
            </a>

            <a
              href="https://x.com/ArigloHarbal"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
            >
              <Twitter size={14} />
            </a>

            <a
              href="https://www.youtube.com/@Ariglo-harbal"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
            >
              <Youtube size={14} />
            </a>
          </div>
        </div>
      </div>

      {/* NAVBAR */}
      <header className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="container nav-inner">
          {/* LOGO */}
          <button type="button" className="logo logo-btn" onClick={() => navigate("/")} aria-label="Go to home page">
            <Leaf size={30} />
            <span>Ariglo</span>
          </button>

          {/* MENU */}
          <nav className="menu desktop">
            <NavLink to="/" end className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Home</NavLink>
            <NavLink to="/about" className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>About</NavLink>
            <NavLink to="/products" className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Products</NavLink>
            <NavLink to="/blog" className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Blog</NavLink>
            <NavLink to="/careers" className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Careers</NavLink>
            <div className="menu-dropdown" ref={categoryRef}>
              <button
                type="button"
                className="menu-link menu-dropdown-trigger"
                onClick={() => setCategoryOpen((prev) => !prev)}
                aria-expanded={categoryOpen}
                aria-haspopup="true"
              >
                Categories <ChevronDown size={14} />
              </button>
              <div className={`menu-dropdown-list ${categoryOpen ? "open" : ""}`}>
                {categoryLinks.map((category) => (
                  <button key={category} type="button" onClick={() => handleCategoryClick(category)}>
                    {category}
                  </button>
                ))}
              </div>
            </div>
            <NavLink to="/contact" className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Contact</NavLink>
          </nav>

          {/* ACTIONS */}
          <div className="actions">
            <div className={`search-wrap ${searchOpen ? "open" : ""}`}>
              {searchOpen && (
                <form onSubmit={handleSearchSubmit}>
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    aria-label="Search products"
                  />
                </form>
              )}
              <button type="button" className="search-btn" onClick={() => setSearchOpen((prev) => !prev)} aria-label="Toggle search">
                <Search size={18} />
              </button>
            </div>

            <a className="cta-whatsapp" href="https://wa.me/919753346858" target="_blank" rel="noreferrer">
              <MessageCircle size={16} /> WhatsApp
            </a>

            {/* CART */}
            <Link to="/cart" className="cart-icon">
              <ShoppingCart size={22} />
              {totalItems > 0 && (
                <span className="cart-badge">{totalItems}</span>
              )}
            </Link>

            {/* LOGIN / USER */}
            {!isAuthenticated ? (
              <button className="btn-outline" onClick={() => setShowLogin(true)}>
                Login
              </button>
            ) : (
              <div className="user-area" ref={dropdownRef}>
                <button type="button" className="avatar" onClick={() => setUserMenu(!userMenu)} aria-label="Open user menu">
                  <User size={18} />
                </button>

                {userMenu && (
                  <div className="user-dropdown">
                    <button
                      onClick={() =>
                        navigate("/dashboard", { state: { fromNav: true } })
                      }
                    >
                      Dashboard
                    </button>

                    <button className="logout" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                )}

              </div>
            )}

            {/* MOBILE */}
            <button className="mobile-btn" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {menuOpen && (
          <div className="menu mobile">
            <NavLink to="/" end onClick={() => setMenuOpen(false)} className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Home</NavLink>
            <NavLink to="/about" onClick={() => setMenuOpen(false)} className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>About</NavLink>
            <NavLink to="/products" onClick={() => setMenuOpen(false)} className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Products</NavLink>
            <NavLink to="/blog" onClick={() => setMenuOpen(false)} className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Blog</NavLink>
            <NavLink to="/careers" onClick={() => setMenuOpen(false)} className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Careers</NavLink>
            <div className="mobile-cat-list">
              {categoryLinks.map((category) => (
                <button key={category} type="button" onClick={() => handleCategoryClick(category)}>
                  {category}
                </button>
              ))}
            </div>
            <NavLink to="/contact" onClick={() => setMenuOpen(false)} className={({ isActive }) => `menu-link ${isActive ? "active" : ""}`}>Contact</NavLink>
            <a className="cta-whatsapp mobile" href="https://wa.me/919753346858" target="_blank" rel="noreferrer">
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>
        )}
      </header>

      {/* LOGIN MODAL */}
      {showLogin && (
        <Modal title="Welcome Back" onClose={() => setShowLogin(false)}>
          <p className="modal-subtitle font-sans">Login to manage orders and track your account.</p>
          {/* <input placeholder="Email" /> */}
          <input
            placeholder="Email"
            value={loginForm.email}
            onChange={(e) =>
              setLoginForm({ ...loginForm, email: e.target.value })
            }
            required
          />
          {/* <input type="password" placeholder="Password" /> */}
          <input
            type="password"
            placeholder="Password"
            value={loginForm.password}
            onChange={(e) =>
              setLoginForm({ ...loginForm, password: e.target.value })
            }
            required
          />
          <button
            type="button"
            className="btn-text btn-text-right"
            onClick={() => {
              setShowLogin(false);
              setShowForgot(true);
            }}
          >
            Forgot Password?
          </button>
          <button className="btn-fill full" onClick={handleLogin}>
            Login
          </button>
          <button type="button" className="btn-text" onClick={() => { setShowLogin(false); setShowRegister(true); }}>
            New user? <span>Create account</span>
          </button>
        </Modal>
      )}

      {/* REGISTER MODAL */}
      {showRegister && (
        <Modal title="Create Account" onClose={() => setShowRegister(false)}>
          <p className="modal-subtitle font-sans">Create your account to access faster checkout.</p>
          {/* <input placeholder="Full Name" />
          <input placeholder="Email" />
          <input type="password" placeholder="Password" /> */}
          <input
            placeholder="Full Name"
            value={registerForm.name}
            onChange={(e) =>
              setRegisterForm({ ...registerForm, name: e.target.value })
            }
            required
          />

          <input
            placeholder="Email"
            value={registerForm.email}
            onChange={(e) =>
              setRegisterForm({ ...registerForm, email: e.target.value })
            }
            required
          />

          <input
            type="password"
            placeholder="Password"
            value={registerForm.password}
            onChange={(e) =>
              setRegisterForm({ ...registerForm, password: e.target.value })
            }
            required
          />
          <button className="btn-fill full" onClick={handleRegister}>Register</button>
          <button type="button" className="btn-text" onClick={() => { setShowRegister(false); setShowLogin(true); }}>
            Already registered? <span>Login</span>
          </button>
        </Modal>
      )}

      {/* FORGOT PASSWORD MODAL */}
      {showForgot && (
        <Modal title="Reset Password" onClose={() => setShowForgot(false)}>
          <p className="modal-subtitle font-sans">We will send a password reset link to your email.</p>
          <input
            placeholder="Enter your email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            required
          />

          {/* <button className="btn-fill full" onClick={handleForgotPassword}>
            Send Reset Link
          </button> */}

          <button
            onClick={handleForgotPassword}
            disabled={forgotLoading}
            className={forgotLoading ? "btn-fill full btn-loading" : "btn-fill full"}
          >
            {forgotLoading ? "Sending..." : "Send Reset Link"}
          </button>

          <button
            type="button"
            className="btn-text btn-text-center"
            onClick={() => {
              setShowForgot(false);
              setShowLogin(true);
            }}
          >
            Back to Login
          </button>
        </Modal>
      )}

      {/* STYLES */}
      <style>{`
        :root{
          --topbar-height: 42px;
          --navbar-height: 74px;
        }


      .social-icon { color: #fff; transition: 0.3s; }
      .social-icon:hover { color: #cfe8cf; transform: translateY(-2px); }

        .btn-text {
          cursor: pointer;
          color: #4a5d4e;
          transition: 0.3s;
          background: transparent;
          border: none;
          text-align: left;
          padding: 0;
        }
        .btn-text-right { text-align: right; font-size: 14px; margin-bottom: 10px; width: 100%; }
        .btn-text-center { margin-top: 10px; text-align: center; width: 100%; }
      .btn-text:hover{ color: var(--brand-main-dark); }
      .modal-subtitle{
        text-align:center;
        color:var(--text-body);
        font-size:13px;
        margin:-2px 0 14px;
      }

      .top-left{
        display:flex;
        align-items:center;
        gap:1.2rem;
        flex-shrink:0;
      }

      .top-center{
        color:rgba(255,255,255,0.9);
        letter-spacing:.03em;
        font-size:11px;
        flex:1;
        max-width:560px;
        margin:0 1.25rem;
      }

      .offer-marquee{
        width:100%;
        overflow:hidden;
        position:relative;
      }
      .offer-track{
        display:inline-flex;
        align-items:center;
        gap:2rem;
        white-space:nowrap;
        min-width:max-content;
        animation:topOffersScroll 24s linear infinite;
        will-change:transform;
      }
      .offer-item{
        position:relative;
        display:inline-flex;
        align-items:center;
      }
      .offer-item::after{
        content:"•";
        margin-left:2rem;
        color:rgba(255,255,255,0.55);
      }
      .offer-track:hover{
        animation-play-state:paused;
      }

      @keyframes topOffersScroll{
        0%{transform:translateX(0)}
        100%{transform:translateX(-50%)}
      }

      .top-right{
        display:flex;
        align-items:center;
        gap:.9rem;
      }

        *{box-sizing:border-box}
        .container{max-width:1280px;margin:auto;padding:0 1.5rem}

        .topbar{
          background:linear-gradient(90deg, #2f4b37, #3f5f47);color:#fff;font-size:12px;
          position:fixed;top:0;width:100%;z-index:100
        }
        .topbar-inner{
          display:flex;
          align-items:center;
          justify-content:space-between;
          min-height:var(--topbar-height);
          padding:.58rem 0
        }
        .top-right{
          justify-content:flex-end;
          flex-shrink:0;
        }

        .top-left span{
          display:inline-flex;
          align-items:center;
          gap:.35rem;
          font-size:13px;
        }

        .top-left svg,
        .top-right svg{
          width:15px;
          height:15px;
        }

        .navbar{
          position:fixed;top:var(--topbar-height);width:100%;
          background: linear-gradient(130deg, rgba(247,252,248,0.92) 0%, rgba(250,248,245,0.92) 52%, rgba(241,232,220,0.92) 100%);
          backdrop-filter:blur(14px);
          border-bottom:1px solid rgba(183, 203, 187, 0.45);
          z-index:200;
        }
        .navbar.scrolled{
          background: linear-gradient(130deg, rgba(244,251,246,0.96) 0%, rgba(250,248,245,0.96) 52%, rgba(241,232,220,0.96) 100%);
          box-shadow:0 10px 24px rgba(37, 60, 43, .08)
        }

        .nav-inner{
          display:flex;
          align-items:center;
          justify-content:space-between;
          height:var(--navbar-height)
        }
        .logo{display:flex;gap:.5rem;font-size:22px;font-weight:700;color:var(--text-strong);cursor:pointer;align-items:center}
        .logo-btn{background:transparent;border:none}
        .logo svg{color:var(--brand-main)}
        .menu.desktop{display:flex;gap:2.5rem}
        .menu-dropdown{position:relative}
        .menu-dropdown-trigger{
          display:flex;
          align-items:center;
          gap:.25rem;
          border:none;
          background:transparent;
          cursor:pointer;
          font-size:inherit;
        }
        .menu-dropdown-list{
          position:absolute;
          top:calc(100% + 2px);
          left:0;
          min-width:170px;
          padding:.35rem;
          border-radius:12px;
          border:1px solid var(--border-soft);
          background:#fff;
          box-shadow:0 14px 28px rgba(32, 48, 37, 0.1);
          display:none;
          z-index:99;
        }
        .menu-dropdown-list.open{display:block}
        .menu-dropdown-list button{
          width:100%;
          border:none;
          background:transparent;
          text-align:left;
          padding:.52rem .6rem;
          border-radius:8px;
          color:var(--text-body);
          cursor:pointer;
        }
        .menu-dropdown-list button:hover{background:#f2f7f3;color:var(--brand-main-dark)}
        .menu-link{
          text-decoration:none;
          color:var(--text-body);
          font-weight:500;
          position:relative;
          padding:.2rem 0;
          transition:color .2s ease;
        }
        .menu-link:hover{color:var(--brand-main)}
        .menu-link.active{color:var(--brand-main);font-weight:600}
        .menu-link.active::after{
          content:"";
          position:absolute;
          left:0;
          bottom:-6px;
          width:100%;
          height:2px;
          background:linear-gradient(90deg, var(--brand-main), #7aa486);
          border-radius:99px;
        }

        .actions{display:flex;align-items:center;gap:1rem}
        .search-wrap{
          display:flex;
          align-items:center;
          gap:.35rem;
          background:rgba(255,255,255,0.78);
          border:1px solid var(--border-soft);
          border-radius:999px;
          padding:.25rem;
        }
        .search-wrap form{display:none}
        .search-wrap.open form{display:block}
        .search-wrap input{
          border:none;
          background:transparent;
          padding:.3rem .55rem;
          min-width:145px;
          color:var(--text-strong);
        }
        .search-wrap input:focus{outline:none}
        .search-btn{
          width:30px;height:30px;border-radius:50%;
          border:none;background:var(--bg-soft);color:var(--brand-main);
          display:flex;align-items:center;justify-content:center;cursor:pointer;
        }
        .cta-whatsapp{
          display:inline-flex;
          align-items:center;
          gap:.35rem;
          background:linear-gradient(90deg, #3a9157, #53a86d);
          color:#fff;
          text-decoration:none;
          border-radius:999px;
          padding:.45rem .85rem;
          font-size:.82rem;
          font-weight:600;
        }
        .cta-whatsapp.mobile{justify-content:center;margin-top:.3rem}
        .cart-icon{
          position:relative;
          color:var(--brand-main);
          width:40px;height:40px;border-radius:50%;
          display:flex;align-items:center;justify-content:center;
          background:rgba(255,255,255,0.72);
          border:1px solid var(--border-soft);
        }
        .cart-badge{
          position:absolute;top:-6px;right:-10px;
          background:var(--brand-main);color:#fff;font-size:11px;
          width:18px;height:18px;border-radius:50%;
          display:flex;align-items:center;justify-content:center
        }

        .btn-outline{
          border:1px solid rgba(63,95,71,.45);background:rgba(255,255,255,0.7);
          color:var(--brand-main);
          padding:.45rem 1.2rem;border-radius:999px;cursor:pointer;
          font-weight:600;
          box-shadow:0 8px 20px rgba(56, 85, 64, 0.08)
        }
        .btn-fill{
          background:linear-gradient(90deg, var(--brand-main), #6f9279);
          color:#fff;border:none;
          padding:.7rem 1.2rem;border-radius:999px;cursor:pointer;
          margin-bottom: 5%;
          font-weight:600;
          box-shadow:0 10px 22px rgba(49,78,58,0.18)
        }
        .btn-loading{opacity:.6}

        .user-area{position:relative}
        .avatar{
          width:34px;height:34px;border-radius:50%;
          background:#4a5d4e;color:#fff;
          display:flex;align-items:center;justify-content:center;
          cursor:pointer;
          border:none;
        }

        .user-dropdown{
          position:absolute;right:0;top:45px;
          background:#fff;border-radius:12px;
          box-shadow:0 10px 25px rgba(0,0,0,.15);
          min-width:160px;overflow:hidden;z-index:9999
        }
        .user-dropdown button{
          width:100%;padding:.75rem 1rem;
          border:none;background:none;text-align:left;cursor:pointer
        }
        .user-dropdown button:hover{background:#f5f5f5}
        .logout{color:#c44;font-weight:600}

        .mobile-btn{
          display:none;background:rgba(255,255,255,0.72);border:1px solid var(--border-soft);
          width:40px;height:40px;border-radius:50%;align-items:center;justify-content:center;
          color:var(--brand-main)
        }
        .menu.mobile{
          background:#fbfdfb;padding:1rem 1.5rem 1.3rem;
          display:flex;flex-direction:column;gap:.95rem;
          border-top:1px solid var(--border-soft);
          box-shadow:0 12px 25px rgba(0,0,0,.06)
        }
        .mobile-cat-list{
          display:grid;
          gap:.4rem;
          margin-top:-.2rem;
        }
        .mobile-cat-list button{
          text-align:left;
          border:1px solid var(--border-soft);
          background:#fff;
          color:var(--text-body);
          border-radius:10px;
          padding:.5rem .7rem;
          cursor:pointer;
        }

        .overlay{
          position:fixed;inset:0;background:rgba(17,25,20,.45);
          backdrop-filter: blur(2px);
          z-index:3000;display:flex;align-items:center;justify-content:center
        }
        .modal{
          background:linear-gradient(180deg, #ffffff, #f9fcf9);
          border:1px solid var(--border-soft);
          border-radius:18px;
          padding:1.5rem 1.2rem 1.2rem;
          width:100%;max-width:390px;
          box-shadow:0 22px 45px rgba(22,40,29,.18)
        }
        .modal-close-btn{
          float:right;
          border:none;
          background:transparent;
          cursor:pointer;
          color:var(--text-muted);
          margin-bottom:2px;
        }
        .modal-title{
          text-align:center;
          margin-bottom:5%;
          color:var(--text-strong);
        }
        .modal input{
          width:100%;padding:.78rem .9rem;margin-bottom:.9rem;
          border-radius:12px;border:1px solid var(--border-soft);
          background:#fff;
          color:var(--text-strong)
        }
        .modal input:focus{
          outline:none;
          border-color:var(--brand-main);
          box-shadow:0 0 0 3px rgba(63,95,71,.12);
        }
        .full{width:100%}

        @media(max-width:768px){
          .topbar{display:none}
          .navbar{top:0}
          .menu.desktop{display:none}
          .mobile-btn{display:flex}
          .actions{gap:.6rem}
          .search-wrap{display:none}
          .cta-whatsapp{display:none}
          .btn-outline{display:none}
        }
      `}</style>
    </>
  );
}

/* MODAL */
function Modal({ title, children, onClose }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="modal-close-btn"
          aria-label="Close modal"
        >
          <X size={18} />
        </button>
        <h2 className="modal-title">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
